import { useNavigate } from 'react-router-dom'

import { Box, Typography, CardMedia } from '@mui/material'
import defaultImage from 'assets/images/default_avatar.png'

import { renderAttendanceType, renderDay } from 'components/custom/AdxLabelRenderer'
import AttendanceLabelRenderer from 'components/custom/AdxLabelRenderer'

import { ReactComponent as StopwatchIcon } from 'assets/icons/svg/Stopwatch.svg'
import AdxTimelineViewer from 'components/custom/AdxTimelineViewer'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxButton from 'components/custom/AdxButton'
import * as React from 'react'

const AttendanceInfoDrawer = ({
  drawerState,
  setDrawerState,
  attendanceDetails,
  disableViewStaff = false,
}) => {
  const navigate = useNavigate()

  const timeline = [
    {
      time: attendanceDetails?.clockInTime,
      location: attendanceDetails?.clockInLocation,
      note: attendanceDetails?.clockInNote,
      coordinates: {
        latitude: attendanceDetails?.clockInLatitude || '3.139000', // Fallback for testing
        longitude: attendanceDetails?.clockInLongitude || '101.686900' // Fallback for testing
      }
    },
    {
      time: attendanceDetails?.clockOutTime,
      location: attendanceDetails?.clockOutLocation,
      note: attendanceDetails?.clockOutNote,
      coordinates: {
        latitude: attendanceDetails?.clockOutLatitude || '3.139000', // Fallback for testing
        longitude: attendanceDetails?.clockOutLongitude || '101.686900' // Fallback for testing
      }
    },
  ]

  const handleDrawer = (anchor, open) => (event) => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  return (
    <AdxDrawerComponent
      noPaddingContent
      state={drawerState}
      anchor="right"
      onClose={handleDrawer}
      headerprops={
        <Box
          sx={{
            display: 'flex',
            flex: 'row',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {renderDay(attendanceDetails?.date) || 'Date Not Available'}
          {!disableViewStaff && (
            <AdxButton
              variant={'underlined'}
              size={'tiny'}
              onClick={() => navigate(`individual/${attendanceDetails?.staffId}`)}
            >
              <Typography sx={{ textDecoration: 'underline' }}> View Staff</Typography>
            </AdxButton>
          )}
        </Box>
      }
      sx={{ width: '500px' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            gap: 2,
            borderBottom: 1,
            borderColor: 'gray-line.main',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'start' }}>
            <CardMedia
              component="img"
              sx={{
                height: 65,
                width: 65,
                objectFit: 'cover',
                borderRadius: '50%',
              }}
              src={attendanceDetails?.profile_image || defaultImage}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              maxWidth: '82%',
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: 'dark-text.main',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {attendanceDetails?.staffName || 'Staff Name Not Available'}{' '}
              </Typography>
              <Typography sx={{ color: 'light-gray-text.main', fontWeight: 400 }}>
                {attendanceDetails?.staffTitle || 'Title Not Available'},{' '}
                {attendanceDetails?.staffDept || 'Department Not Available'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <AttendanceLabelRenderer
                status={attendanceDetails?.status}
                withText
                isHoliday={attendanceDetails?.isHoliday}
              />
              {attendanceDetails?.status !== 'leave' && (
                <>
                  <Typography
                    sx={{ alignSelf: 'center', color: 'light-gray-text.main', fontWeight: 600 }}
                  >
                    •
                  </Typography>
                  {renderAttendanceType(attendanceDetails?.type)}
                </>
              )}
            </Box>
          </Box>
        </Box>
        {/* Attendance Details */}
        {attendanceDetails?.status === 'manual_add' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: 2,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                border: '1px solid',
                borderColor: 'gray-line.main',
                borderRadius: '4.5px',
              }}
            >
              <Box sx={{ borderBottom: '1px solid', borderColor: 'gray-line.main', p: 2 }}>
                <Typography
                  sx={{
                    color: 'light-gray-text.main',
                    fontVariant: 'subtitle1',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  Today's total
                </Typography>
                <Typography sx={{ color: 'dark-text.main', fontSize: '16px', fontWeight: 500 }}>
                  {attendanceDetails?.duration}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    width: '50%',
                    borderRight: '1px solid',
                    borderColor: 'gray-line.main',
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{
                      color: 'light-gray-text.main',
                      fontVariant: 'subtitle1',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    Clock in
                  </Typography>
                  <Typography sx={{ color: 'dark-text.main', fontSize: '16px', fontWeight: 500 }}>
                    {attendanceDetails?.clockInTime}
                  </Typography>
                </Box>
                <Box sx={{ width: '50%', p: 2 }}>
                  <Typography
                    sx={{
                      color: 'light-gray-text.main',
                      fontVariant: 'subtitle1',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    Clock out
                  </Typography>
                  <Typography sx={{ color: 'dark-text.main', fontSize: '16px', fontWeight: 500 }}>
                    {attendanceDetails?.clockOutTime}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: 2,
              }}
            >
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography>Reason</Typography>
                <Typography
                  sx={{
                    color: 'light-gray-text.main',
                    fontVariant: 'subtitle1',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {attendanceDetails?.clockInLocation}
                </Typography>
              </Box>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography>Proof</Typography>
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    border: '1px solid black',
                    borderRadius: '4.5px',
                  }}
                >
                  File Thumbnail here
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: 2,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                border: '1px solid',
                borderColor: 'gray-line.main',
                borderRadius: '4.5px',
                p: 2,
              }}
            >
              <Box
                sx={{
                  width: '45px',
                  height: '45px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%',
                  backgroundColor: 'alternate.light',
                  color: 'alternate.main',
                }}
              >
                <StopwatchIcon />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: 'light-gray-text.main',
                    fontVariant: 'subtitle1',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  Total hours
                </Typography>
                <Typography sx={{ color: 'dark-text.main', fontSize: '16px', fontWeight: 500 }}>
                  {attendanceDetails?.duration ? attendanceDetails?.duration : '--:--'} hrs
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: 'light-gray-text.main',
                    fontVariant: 'subtitle1',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  Break time
                </Typography>
                <Typography sx={{ color: 'dark-text.main', fontSize: '16px', fontWeight: 500 }}>
                  {attendanceDetails?.breakTime ? attendanceDetails?.breakTime : '00:00'} hrs
                </Typography>
              </Box>
            </Box>
            <AdxTimelineViewer timeline={timeline} />
          </Box>
        )}
      </Box>
    </AdxDrawerComponent>
  )
}

export default AttendanceInfoDrawer
